import { ExclamationCircleFilled } from '@ant-design/icons';
import { Button, Input, Modal, Select } from 'antd';
import parsePhoneNumberFromString, { ParseError } from 'libphonenumber-js';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { IntlTelInput } from '@/components/shared/IntlTelInput';

import { getGlobalCountry } from '@/atoms/countryState';

import ButtonDefault, { ButtonVariants } from '../../shared/basic/button';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import { formateErrorObject } from '../../shared/formateErrorObject';
import { notify } from '../../shared/notify';
import { user } from '../../../atoms/user';
import { useAddresses } from '../../../hooks/useAddresses';
import {
  deleteAddress,
  getAddresses,
  updateAddress,
  updateAddressRadioButtonSelect,
} from '../../../services/Address';
import {
  Address,
  AddressPayload,
  AddressType,
} from '../../../utils/interfaces/Manage-Addresses';
import { User } from '../../../utils/interfaces/User';

interface Props {
  data: {
    id: number;
    fullname: string;
    email: string;
    postalcode: string;
    set_default: number;
    country: string;
    city: string;
    state: string;
    address: string;
    country_code?: string;
    iso2?: string;
    phone?: string;
  };
  edit: { state: boolean; type: string };
  onDelete: (id: number) => void;
  setEdit: (newEdit: { state: boolean; type: string }) => void;
  formType: string;
  selectedAddress: number | null;
  setSelectedAddress: (value: number | null) => void;
  setAddresses: (value: Address[]) => void;
}

const AddressCard: React.FC<Props> = ({ data, edit, setAddresses }) => {
  const {
    getAllCountries,
    getStates,
    states,
    fieldLoading,
    countries,
    cities,
    getCities,
    createAddress,
    createLoading,
  } = useAddresses();
  const [formData, setFormData] = useState({
    ...data,
    ...(edit?.state && { country: 'India' }),
  });
  const userData: User | undefined = useRecoilValue(user);

  const [editState, setEditState] = useState(edit?.state || false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [errors, setErrors] = useState({
    fullname: '',
    postalcode: '',
    phone: '',
    country: '',
    city: '',
    state: '',
    address: '',
    makeAsDefault: 0,
  });
  const [isModalOpenDelete, setIsModalOpenDelete] = useState(false);
  const globalCountry = getGlobalCountry();

  const showModal = () => {
    setIsModalOpenDelete(true);
  };

  const handleOk = () => {
    handleDeleteAddress(data?.id);
  };

  const handleCancel = () => {
    setIsModalOpenDelete(false);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSelectChange = (value: string, name: string) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleDelete = () => {
    showModal();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const validationErrors = {
      fullname: '',
      email: '',
      postalcode: '',
      country: '',
      city: '',
      phone: '',
      state: '',
      address: '',
      makeAsDefault: 0,
    };

    if (!formData.fullname) {
      validationErrors.fullname = 'Full name is required';
    }

    if (!formData.postalcode) {
      validationErrors.postalcode = 'Postal Code is required';
    } else if (
      formData.country === 'India' &&
      !isValidPostalCode(formData.postalcode)
    ) {
      validationErrors.postalcode = 'Enter valid postal code';
    }
    if (!formData.country) {
      validationErrors.country = 'Please select country';
    }
    if (!formData.city) {
      validationErrors.city = 'City is required';
    }
    if (!formData.phone) {
      validationErrors.phone = 'Phone number is required';
    }
    if (!formData.state) {
      validationErrors.state = 'State is required';
    }
    if (!formData.address) {
      validationErrors.address = 'Address is required';
    }

    setErrors(validationErrors);

    if (Object.values(validationErrors).every((error) => !error)) {
      try {
        const selectedState =
          typeof formData?.state === 'string'
            ? String(
                states?.find((item) => item.label === formData.state)?.value,
              )
            : formData?.state;
        const selectedCity =
          typeof formData?.city === 'string'
            ? String(
                cities?.find((item) => item.label === formData.city)?.value,
              )
            : formData?.city;
        const selectedCountry =
          typeof formData?.country === 'string'
            ? String(
                countries?.find((item) => item.label === formData.country)
                  ?.value,
              )
            : formData?.country;

        const data = {
          user_id: userData?.id || 0,
          set_default: formData?.set_default,
          name: formData?.fullname,
          address: formData?.address,
          country_id: selectedCountry,
          city_id: selectedCity,
          state_id: selectedState,
          postal_code: formData?.postalcode,
          phone: formData?.phone || '',
          country_code: formData?.country_code,
          iso2: formData?.iso2,
        };
        if (edit.type === 'Add Address') {
          await handleCreate(data);
        } else {
          await handleUpdate(data);
        }
      } catch (error) {
        if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
          console.error('Error initializing messaging:', error);
        }
      }
    }
  };

  const handleCreate = async (data: AddressPayload) => {
    try {
      await createAddress(data);
      const address = await getAddresses();
      const formattedAddresses = address?.map((address: AddressType) => ({
        id: address.id,
        fullname: address?.name,
        email: 'address.email',
        postalcode: address.postal_code,
        country: address.country_name,
        phone: address?.phone,
        city: address.city_name,
        state: address.state_name,
        address: address.address,
        makeAsDefault: false,
        edit: { state: false, type: '' },
      }));

      setAddresses(formattedAddresses as Address[]);
      notify(`Address Added Successfully!!`, 'success');
      setEditState(false);
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  const handleUpdate = async (payload?: AddressPayload, isRadio?: boolean) => {
    try {
      setUpdateLoading(true);

      if (isRadio) {
        await updateAddressRadioButtonSelect({
          id: data?.id,
          set_default: 1,
        });
      } else {
        await updateAddress({ ...payload, id: data?.id } as AddressPayload);
      }

      const updatedAddresses = await getAddresses();
      const formattedAddresses = updatedAddresses?.map(
        (address: AddressType) => ({
          id: address.id,
          fullname: address?.name,
          email: 'address.email',
          postalcode: address.postal_code,
          country: address.country_name,
          phone: address?.phone,
          set_default: address?.set_default,
          city: address.city_name,
          state: address.state_name,
          address: address.address,
          makeAsDefault: false,
          edit: { state: false, type: '' },
        }),
      );

      setAddresses(formattedAddresses as Address[]);
      notify(`Address Edited Successfully!!`, 'success');
      setEditState(false);
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteAddress = async (id: number) => {
    try {
      setDeleteLoading(true);
      await deleteAddress(id);
      const address = await getAddresses();
      const formattedAddresses = address?.map((address: AddressType) => ({
        id: address.id,
        fullname: address?.name,
        email: 'address.email',
        postalcode: address.postal_code,
        country: address.country_name,
        phone: address?.phone,
        set_default: address?.set_default,
        city: address.city_name,
        state: address.state_name,
        address: address.address,
        makeAsDefault: false,
        edit: { state: false, type: '' },
      }));
      setAddresses(formattedAddresses as Address[]);
      setIsModalOpenDelete(false);
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePostalCodeInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value, name } = e.target;
    if (name === 'phone') {
      const inputValue = value;
      const cleanedValue = inputValue.replace(/\D/g, '');
      setFormData({ ...formData, phone: cleanedValue });
    }
    if (name === 'postalcode') {
      const inputValuePostalcode = value;
      const cleanedValue = inputValuePostalcode.replace(/\D/g, '');
      setFormData({
        ...formData,
        postalcode: formData.country === 'India' ? cleanedValue : value,
      });
    }
  };

  const isValidPostalCode = (value: string) => {
    const phoneRegex = /^\d{6}$/;

    return phoneRegex.test(value);
  };

  useEffect(() => {
    if (editState) {
      getAllCountries();
    }
  }, [editState]);

  useEffect(() => {
    if (formData?.country && editState && countries) {
      const selectedCountry =
        typeof formData?.country === 'string'
          ? String(
              countries?.find((item) => item.label === formData.country)?.value,
            )
          : formData?.country;
      if (selectedCountry && selectedCountry !== 'undefined') {
        getStates(selectedCountry);
      }
    }
  }, [formData?.country, editState, countries]);

  useEffect(() => {
    if (formData?.state && editState && states) {
      const selectedState =
        typeof formData?.state === 'string'
          ? String(states?.find((item) => item.label === formData.state)?.value)
          : formData?.state;
      if (selectedState && selectedState !== 'undefined') {
        getCities(selectedState);
      }
    }
  }, [formData?.state, editState, states]);

  const filterOption = (
    input: string,
    option?: { label: string; value: string | number },
  ) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  const handlePhoneInput = ({
    phone,
    phoneCountryCode,
    phonePrefix,
  }: {
    phone: string;
    phoneCountryCode: string;
    phonePrefix: string;
  }) => {
    const cleanedValue = phone.replace(/\D/g, '');

    setFormData({
      ...formData,
      phone: cleanedValue,
      iso2: phoneCountryCode,
      country_code: phonePrefix,
    });

    try {
      const parsedPhoneNumber = parsePhoneNumberFromString(
        '+' + phonePrefix + cleanedValue,
      );

      if (!cleanedValue.trim()) {
        setErrors({
          ...errors,
          phone: 'Phone number is required',
        });
      } else if (!parsedPhoneNumber) {
        setErrors({
          ...errors,
          phone: 'Invalid phone number format',
        });
      } else if (!parsedPhoneNumber.isValid()) {
        setErrors({
          ...errors,
          phone: 'Invalid phone number. Please check the number and try again.',
        });
      } else {
        setErrors({
          ...errors,
          phone: '',
        });
      }
    } catch (error) {
      if (error instanceof ParseError) {
        setErrors({
          ...errors,
          phone: error.message,
        });
      } else {
        setErrors({
          ...errors,
          phone:
            'An error occurred while validating the phone number. Please try again.',
        });
      }
    }
  };

  return (
    <>
      <div className='flex flex-col gap-y-1 '>
        <div className='flex flex-col gap-y-4'>
          <span>
            <span className='py-6  px-4 xs:px-8 flex flex-col gap-y-2 text-fadedWhite bg-[#151515] rounded-md cardShadow mt-4  hover:cursor-pointer'>
              {editState ? (
                <div className='flex gap-x-2'>
                  <div className='flex flex-col gap-4 w-full'>
                    <div className='mt-2 flex flex-col md:flex-row gap-y-2 justify-between'>
                      <div className='font-medium'>
                        {edit.type ? edit.type : 'Edit Address'}
                      </div>
                      <div className='flex gap-x-2'>
                        <ShouldRender check={edit?.type !== 'Add Address'}>
                          <ButtonDefault
                            size={1}
                            variant={ButtonVariants.UNSTYLED}
                            className='w-full md:w-min p-1 rounded-md disabled:cursor-not-allowed border border-[#3A3A3A] text-fadedWhite bg-[#151515] hover:bg-[#1d1d1d]  disabled:bg-dropDownMenuBg disabled:text-[#3E3E3E] disabled:border-0'
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setEditState(!editState);
                            }}
                          >
                            <span className='flex text-sm gap-x-2 px-4  whitespace-nowrap'>
                              Edit
                            </span>
                          </ButtonDefault>
                        </ShouldRender>
                        <ButtonDefault
                          size={1}
                          variant={ButtonVariants.UNSTYLED}
                          className='w-full md:w-min p-1 rounded-md disabled:cursor-not-allowed border border-[#3A3A3A] text-fadedWhite bg-[#151515] hover:bg-[#1d1d1d]  disabled:bg-dropDownMenuBg disabled:text-[#3E3E3E] disabled:border-0'
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDelete();
                          }}
                        >
                          <span className='flex text-sm gap-x-2 px-2  whitespace-nowrap'>
                            Delete
                          </span>
                        </ButtonDefault>
                      </div>
                    </div>
                    <div className='col-span-1 flex-col flex gap-y-.5'>
                      <label className='  pb-2 l flex gap-x-2'>
                        Full Name <p className='text-red-500'>*</p>{' '}
                      </label>
                      <Input
                        name='fullname'
                        value={formData.fullname}
                        onChange={handleChange}
                        style={{
                          backgroundColor: '#000000',
                        }}
                        size='large'
                        className=' text-sm rounded-lg focus-within:bg-bgColor border border-bgDarkGray  placeholder:text-textDarkGrayColor  py-3  text-white h-10 lg:h-12 bg-bgColor  makeBgBlack'
                      />
                      {errors.fullname && (
                        <p className='text-red-500'>{errors.fullname}</p>
                      )}
                    </div>
                    <div className='col-span-1 flex-col flex gap-y-.5'>
                      <label className='text-base pb-2'>Phone</label>
                      <IntlTelInput
                        phone={formData?.phone || ''}
                        phoneCountryCode={
                          formData?.iso2 ||
                          globalCountry?.countryCode?.toLowerCase() ||
                          'in'
                        }
                        onChange={({
                          phone,
                          phoneCountryCode,
                          phonePrefix,
                        }) => {
                          const cleanedPhone = phone.replace(/^0+/, '');
                          handlePhoneInput({
                            phone: cleanedPhone,
                            phoneCountryCode,
                            phonePrefix,
                          });
                        }}
                      />
                      {errors.phone && (
                        <p className='text-red-500'>{errors.phone}</p>
                      )}
                    </div>
                    <div className='col-span-1 flex-col flex gap-y-.5'>
                      <label className='pb-2 l flex gap-x-2'>
                        Country <p className='text-red-500'>*</p>{' '}
                      </label>
                      <Select
                        style={{ height: 40 }}
                        showSearch
                        value={formData.country || '101'}
                        filterOption={filterOption}
                        loading={fieldLoading.country}
                        placeholder='Select Country'
                        onChange={(value) =>
                          handleSelectChange(value, 'country')
                        }
                        options={countries}
                        className=' antdSelect antdSelectDropdown'
                      />
                      {errors.country && (
                        <p className='text-red-500'>{errors.country}</p>
                      )}
                    </div>

                    <div className='grid md:grid-cols-2  gap-4'>
                      <div className='col-span-1 flex-col flex gap-y-.5'>
                        <label className='  pb-2 l flex gap-x-2'>
                          State <p className='text-red-500'>*</p>{' '}
                        </label>
                        <Select
                          style={{ height: 40 }}
                          showSearch
                          disabled={!formData?.country}
                          value={formData.state}
                          filterOption={filterOption}
                          loading={fieldLoading.state}
                          placeholder='Select State'
                          onChange={(value) =>
                            handleSelectChange(value, 'state')
                          }
                          options={states}
                          className=' rounded-none border-gray-600 antdSelect'
                        />
                        {errors.state && (
                          <p className='text-red-500'>{errors.state}</p>
                        )}
                      </div>
                      <div className='col-span-1 flex-col flex gap-y-.5'>
                        <label className='  pb-2 l flex gap-x-2'>
                          City <p className='text-red-500'>*</p>{' '}
                        </label>
                        <Select
                          style={{ height: 40 }}
                          showSearch
                          disabled={!formData?.state}
                          value={formData.city}
                          filterOption={filterOption}
                          loading={fieldLoading.city}
                          placeholder='Select City'
                          onChange={(value) =>
                            handleSelectChange(value, 'city')
                          }
                          options={cities}
                          className=' rounded-none border-gray-600 antdSelect'
                        />
                        {errors.city && (
                          <p className='text-red-500'>{errors.city}</p>
                        )}
                      </div>
                    </div>
                    <div className='col-span-1 flex-col flex gap-y-.5'>
                      <label className='  pb-2 l flex gap-x-2'>
                        Postal code <p className='text-red-500'>*</p>{' '}
                      </label>
                      <Input
                        name='postalcode'
                        value={formData.postalcode}
                        onChange={handlePostalCodeInput}
                        type='tel'
                        autoComplete='tel'
                        inputMode='numeric'
                        style={{
                          backgroundColor: '#000000',
                        }}
                        size='large'
                        className=' text-sm rounded-lg focus-within:bg-bgColor border border-bgDarkGray  placeholder:text-textDarkGrayColor  py-3  text-white h-10 lg:h-12 bg-bgColor  makeBgBlack'
                      />
                      {errors.postalcode && (
                        <p className='text-red-500'>{errors.postalcode}</p>
                      )}
                    </div>
                    <div className='col-span-2 flex-col flex gap-y-.5'>
                      <label className='  pb-2 l flex gap-x-2'>
                        Address <p className='text-red-500'>*</p>{' '}
                      </label>
                      <Input.TextArea
                        name='address'
                        value={formData.address}
                        onChange={handleChange}
                        minLength={6}
                        style={{
                          backgroundColor: '#000000',
                        }}
                        size='large'
                        className=' text-sm rounded-lg focus-within:bg-bgColor border border-bgDarkGray  placeholder:text-textDarkGrayColor  py-3  text-white h-10 lg:h-12 bg-bgColor  makeBgBlack'
                      />
                      {errors.address && (
                        <p className='text-red-500'>{errors.address}</p>
                      )}
                    </div>

                    {/* <div className='col-span-2 flex-col flex gap-y-.5'>
                      <Checkbox
                        name='makeAsDefault'
                        className='text-fadedWhite '
                        checked={formData.set_default === 1 ? true : false}
                        onChange={(e) => handleCheckboxChange(e.target.checked)}
                      >
                        Mark as default
                      </Checkbox>
                      {errors.address && (
                        <p className='text-red-500'>{errors.address}</p>
                      )}
                    </div> */}

                    <ButtonDefault
                      size={1}
                      variant={ButtonVariants.UNSTYLED}
                      className='w-full md:w-min  rounded-md p-1 disabled:cursor-not-allowed border border-[#3A3A3A] text-fadedWhite bg-[#151515] hover:bg-[#1d1d1d]  disabled:bg-dropDownMenuBg disabled:text-[#3E3E3E] disabled:border-0'
                      loading={createLoading || updateLoading}
                      disabled={createLoading || updateLoading}
                      onClick={(e) => {
                        handleSubmit(e);
                      }}
                    >
                      <span className='flex text-sm gap-x-2 px-4  whitespace-nowrap'>
                        {edit.type !== 'Add Address' ? 'Update' : 'Save'}
                      </span>
                    </ButtonDefault>
                  </div>
                </div>
              ) : (
                <div className='flex flex-col lg:flex-row gap-y-4 gap-x-2  justify-between'>
                  <div className='flex gap-x-2'>
                    <div className='flex flex-col text-base gap-y-1'>
                      <span className='text-xl capitalize'>
                        {data.fullname}
                      </span>
                      <div className='inline-flex'>
                        {data.address}, {data.postalcode}, {data.city},{' '}
                        {data.state}, {data.country}
                      </div>
                    </div>
                  </div>
                  <div className='flex items-start'>
                    <div className='mt-2 flex gap-x-2'>
                      <ShouldRender check={edit?.type !== 'Add Address'}>
                        <ButtonDefault
                          size={1}
                          variant={ButtonVariants.UNSTYLED}
                          className='w-full md:w-min p-1 rounded-md disabled:cursor-not-allowed border border-[#3A3A3A] text-fadedWhite bg-[#151515] hover:bg-[#1d1d1d]  disabled:bg-dropDownMenuBg disabled:text-[#3E3E3E] disabled:border-0'
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setEditState(true);
                          }}
                        >
                          <span className='flex text-sm gap-x-2 px-4  whitespace-nowrap'>
                            Edit
                          </span>
                        </ButtonDefault>
                      </ShouldRender>
                      <ButtonDefault
                        size={1}
                        variant={ButtonVariants.UNSTYLED}
                        className='w-full md:w-min p-1 rounded-md disabled:cursor-not-allowed border border-[#3A3A3A] text-fadedWhite bg-[#151515] hover:bg-[#1d1d1d]  disabled:bg-dropDownMenuBg disabled:text-[#3E3E3E] disabled:border-0'
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDelete();
                        }}
                      >
                        <span className='flex text-sm gap-x-2 px-2  whitespace-nowrap'>
                          Delete
                        </span>
                      </ButtonDefault>
                    </div>
                  </div>
                </div>
              )}
            </span>
          </span>
        </div>
      </div>
      <Modal
        open={isModalOpenDelete}
        onOk={handleOk}
        onCancel={handleCancel}
        width={350}
        closable={false}
        className='customMaskModal rounded-2xl'
        footer={null}
      >
        <div className='bg-[#121212] p-2 text-white border rounded-2xl border-[#3D3D3D]  '>
          <div className='w-full flex flex-col gap-y-4 justify-center py-2'>
            <div className='flex justify-center gap-x-2 py-5'>
              <div className=' flex-col flex gap-y-4'>
                <span className='text-center'>
                  <ExclamationCircleFilled
                    className='text-yellow-400'
                    style={{ fontSize: ' 30px' }}
                  />
                </span>
                <span className='text-lg'>
                  Sure want to delete this address ?
                </span>
              </div>
            </div>
            <div className='flex justify-center px-5 md:px-10 gap-x-4'>
              <ButtonDefault
                size={1}
                variant={ButtonVariants.UNSTYLED}
                className='w-full p-1 rounded-md disabled:cursor-not-allowed border border-[#3A3A3A] text-fadedWhite bg-[#151515] hover:bg-[#1d1d1d]  disabled:bg-dropDownMenuBg disabled:text-[#3E3E3E] disabled:border-0'
                onClick={handleCancel}
              >
                <span className='flex text-sm gap-x-2 px-2  whitespace-nowrap'>
                  No
                </span>
              </ButtonDefault>

              <Button
                disabled={deleteLoading}
                loading={deleteLoading}
                type='primary'
                className='w-full'
                onClick={handleOk}
              >
                Yes
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AddressCard;
